import { ArrowRight, Download, Plus, Receipt, RotateCcw, Settings2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type {
  WalletTransactionEntity,
  WalletTransactionStatus,
  WalletTransactionType,
} from "../types/wallets-api.types";
import { formatMovementDateTime } from "../utils/cardMovementHelpers";
import { formatAmount, isIncomingDirection, sanitizeDescription } from "../utils/walletMovementFormat";

type WalletMovementsTableProps = {
  movements: WalletTransactionEntity[];
};

type TypeMeta = {
  icon: LucideIcon;
  iconWrapper: string;
  badgeVariant: "success" | "warning" | "destructive" | "primary" | "secondary" | "default";
};

const typeMetaMap: Record<WalletTransactionType, TypeMeta> = {
  TOPUP: {
    icon: Plus,
    iconWrapper: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    badgeVariant: "success",
  },
  DEPOSIT: {
    icon: Plus,
    iconWrapper: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    badgeVariant: "success",
  },
  TRANSFER: {
    icon: ArrowRight,
    iconWrapper: "bg-primary/10 text-primary",
    badgeVariant: "primary",
  },
  WITHDRAWAL: {
    icon: Download,
    iconWrapper: "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300",
    badgeVariant: "destructive",
  },
  FEE: {
    icon: Receipt,
    iconWrapper: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    badgeVariant: "warning",
  },
  REVERSAL: {
    icon: RotateCcw,
    iconWrapper: "bg-muted text-muted-foreground",
    badgeVariant: "secondary",
  },
  ADJUSTMENT: {
    icon: Settings2,
    iconWrapper: "bg-muted text-muted-foreground",
    badgeVariant: "secondary",
  },
};

export const WalletMovementsTable = ({ movements }: WalletMovementsTableProps) => {
  const { t } = useTranslations("cards");

  const typeLabel = (type: WalletTransactionType): string =>
    ({
      TOPUP: t("wallet_movements_type_topup"),
      TRANSFER: t("wallet_movements_type_transfer"),
      WITHDRAWAL: t("wallet_movements_type_withdrawal"),
      FEE: t("wallet_movements_type_fee"),
      DEPOSIT: t("wallet_movements_type_deposit"),
      REVERSAL: t("wallet_movements_type_reversal"),
      ADJUSTMENT: t("wallet_movements_type_adjustment"),
    })[type];

  const statusLabel = (status: WalletTransactionStatus): string =>
    ({
      PENDING: t("wallet_movements_status_pending"),
      COMPLETED: t("wallet_movements_status_completed"),
      FAILED: t("wallet_movements_status_failed"),
      REVERSED: t("wallet_movements_status_reversed"),
    })[status];

  return (
    <div className="divide-y divide-border">
      {movements.map((tx) => {
        const meta = typeMetaMap[tx.transaction_type];
        const Icon = meta.icon;
        const isIncoming = isIncomingDirection(tx.direction);

        return (
          <div
            key={tx.id}
            className="flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-muted/50"
          >
            <span
              className={cn(
                "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md",
                meta.iconWrapper,
              )}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <ColocaBadge variant={meta.badgeVariant}>{typeLabel(tx.transaction_type)}</ColocaBadge>
                {tx.description && (
                  <span className="truncate text-sm text-foreground">
                    {sanitizeDescription(tx.description)}
                  </span>
                )}
              </div>
              <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                <span>{formatMovementDateTime(tx.created_at)}</span>
                <span aria-hidden="true">·</span>
                <span>{statusLabel(tx.status)}</span>
                {tx.created_by && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span className="truncate" title={tx.created_by.full_name}>
                      {tx.created_by.email}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="shrink-0 text-right">
              <div
                className={cn(
                  "font-mono text-sm font-semibold tabular-nums",
                  isIncoming ? "text-emerald-600 dark:text-emerald-400" : "text-foreground",
                )}
              >
                {formatAmount(tx.amount, tx.currency, tx.direction)}
              </div>
              <div className="mt-0.5 text-[11px] text-muted-foreground">{tx.created_at.slice(0, 10)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
